import type { Scenario } from '@orbomb/content';
import type { SimConfig } from './config';
import { CAMP_ORDER, campIndex } from './constants';
import { createMatch } from './simulation';
import type { InputVector, MatchState, TickInputs } from './types';

/**
 * Builds the initial state of a named reproducible scene.
 * Golden balls not listed in the setup are parked (ABSENT, never respawning).
 */
export function applyScenario(config: SimConfig, scenario: Scenario): MatchState {
  const setup = scenario.setup;
  const phase = setup.phase ?? 'REGULATION';
  const state = createMatch(config, {
    seed: scenario.seed,
    countdownTicks: phase === 'COUNTDOWN' ? config.ticks.countdown : 0,
  });
  const clock = state.clock;
  if (phase === 'REGULATION') {
    const remaining = setup.regulationRemainingTicks ?? config.ticks.regulation;
    state.tick = clock.regulationEndTick - remaining;
  } else if (phase === 'OVERTIME') {
    state.tick = clock.regulationEndTick;
    clock.phase = 'OVERTIME';
  } else if (phase === 'SUDDEN_DEATH') {
    const elapsed = setup.suddenDeathElapsedTicks ?? 0;
    state.tick = clock.regulationEndTick + 60 + elapsed;
    clock.phase = 'SUDDEN_DEATH';
    clock.suddenDeathStartTick = state.tick - elapsed;
    if (elapsed >= config.ticks.suddenDeathAutoArmAfter) {
      const since = elapsed - config.ticks.suddenDeathAutoArmAfter;
      const interval = config.ticks.suddenDeathAutoArmInterval;
      clock.nextAutoArmTick = state.tick + ((interval - (since % interval)) % interval);
    }
  }
  if (setup.totemHp) {
    state.totems[0].hp = setup.totemHp.ORANGE;
    state.totems[1].hp = setup.totemHp.TURQUOISE;
  }
  for (const camp of CAMP_ORDER) {
    const spec = setup.players?.[camp];
    if (!spec) continue;
    const p = state.players[campIndex(camp)];
    p.x = spec.x;
    p.y = spec.y;
    p.vx = spec.vx ?? 0;
    p.vy = spec.vy ?? 0;
    p.status = spec.status ?? 'NORMAL';
    const remaining = spec.statusRemainingTicks ?? 1;
    if (p.status === 'STUNNED') {
      p.vx = 0;
      p.vy = 0;
      p.stunUntilTick = state.tick + remaining;
      p.protectedUntilTick = p.stunUntilTick + config.ticks.protection;
    } else if (p.status === 'PROTECTED') {
      p.protectedUntilTick = state.tick + remaining;
    }
  }
  if (setup.goldens) {
    state.goldens.forEach((g, i) => {
      const spec = setup.goldens?.[i];
      if (!spec) {
        g.status = 'ABSENT';
        g.vx = 0;
        g.vy = 0;
        g.x = -10;
        g.y = -10;
        g.respawnAtTick = null;
        return;
      }
      g.x = spec.x;
      g.y = spec.y;
      g.vx = spec.vx ?? 0;
      g.vy = spec.vy ?? 0;
      g.status = spec.status ?? 'NEUTRAL';
      if (g.status === 'ARMED') {
        // fuseRemainingTicks = number of steps until detonation (1 = during the next step).
        g.detonationTick = state.tick + (spec.fuseRemainingTicks ?? config.ticks.fuse) - 1;
        g.armedAtTick = g.detonationTick - config.ticks.fuse;
        g.firstToucher = spec.toucher ?? null;
        g.lastToucher = spec.toucher ?? null;
        g.armedBy = spec.toucher ? 'CONTACT' : 'AUTO';
      }
    });
  }
  return state;
}

const ZERO: InputVector = { x: 0, y: 0 };

/** Inputs for a tick relative to the scenario start. Later spans override earlier ones. */
export function scenarioInputs(scenario: Scenario, relativeTick: number): TickInputs {
  let orange = ZERO;
  let turquoise = ZERO;
  for (const span of scenario.inputs) {
    if (relativeTick < span.from || relativeTick >= span.to) continue;
    if (span.ORANGE) orange = span.ORANGE;
    if (span.TURQUOISE) turquoise = span.TURQUOISE;
  }
  return { ORANGE: orange, TURQUOISE: turquoise };
}
